import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from './ui/card';
import { FlaskConicalIcon } from 'lucide-react';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SandboxModeSettings — toggle UI for the per-user "Sandbox Modu" flag.
 * Visual-only feature: the rest of the app keeps working normally (mails,
 * Stripe etc. unaffected). The banner + header badge are the user's signal
 * that they're in a test session. See /api/auth/me/sandbox-mode.
 */
const SandboxModeSettings = () => {
  const { user, checkAuth } = useAuth();
  const [enabled, setEnabled] = useState(!!user?.sandbox_mode);
  const [saving, setSaving] = useState(false);

  const handleToggle = async () => {
    const next = !enabled;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${BACKEND_URL}/api/auth/me/sandbox-mode`,
        { enabled: next },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setEnabled(next);
      // Refresh auth context so the banner + header badge update immediately.
      if (typeof checkAuth === 'function') await checkAuth();
      toast.success(
        next ? 'Sandbox Modu açıldı 🧪' : 'Sandbox Modu kapatıldı',
      );
    } catch (err) {
      toast.error('Sandbox Modu güncellenemedi');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-3xl" data-testid="sandbox-mode-settings">
      <div className="mb-6 flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
          <FlaskConicalIcon className="w-5 h-5 text-amber-700" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Sandbox Modu</h2>
          <p className="text-sm text-gray-500 mt-1">
            Yeni özellikleri ve akışları denemen için görsel bir test moduna
            geçer. Mailler ve ödemeler normal akar — sadece dashboard üstünde
            sarı bir banner ve header&apos;da bir rozet görürsün.
          </p>
        </div>
      </div>

      <Card className="p-5">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="font-medium text-gray-900">
              {enabled ? 'Sandbox Modu açık' : 'Sandbox Modu kapalı'}
            </div>
            <div className="text-sm text-gray-500 mt-0.5">
              {enabled
                ? 'Üstte sarı uyarı görüyorsun — testleri yaptıktan sonra kapatabilirsin.'
                : 'Açtığında ekran üstünde sarı bir uyarı ve header rozeti çıkar.'}
            </div>
          </div>
          <button
            type="button"
            onClick={handleToggle}
            disabled={saving}
            role="switch"
            aria-checked={enabled}
            data-testid="sandbox-mode-toggle"
            className={`relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 ${
              enabled ? 'bg-amber-500' : 'bg-gray-300'
            } ${saving ? 'opacity-60 cursor-wait' : ''}`}
          >
            <span
              aria-hidden="true"
              className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                enabled ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      </Card>

      <div className="mt-4 text-xs text-gray-500 leading-relaxed">
        💡 Bu mod yalnızca görsel bir hatırlatıcıdır — gerçek hiçbir veriyi
        değiştirmez. Sandbox altında oluşturduğun harcama / fatura kayıtları
        canlı veritabanına yazılır; istediğin gibi sil/düzenle.
      </div>
    </div>
  );
};

export default SandboxModeSettings;
